import io from 'socket.io-client';
import {SOCKET_ENDPOINT} from './constants';

const socket = io(SOCKET_ENDPOINT, {
	reconnection: true,
	reconnectionDelay: 500,
	reconnectionAttempts: 10,
});

const IDENTITY_KEY = 'literate-identity';
const ACTIVE_ROOM_KEY = 'literate-active-room-key';
const ACTIVE_ROLE_KEY = 'literate-active-role';

const hasStorage = () => typeof Storage !== 'undefined';

const removeListenerSafe = (target, event, handler) => {
	if (!target) return;
	if (typeof target.off === 'function') {
		target.off(event, handler);
		return;
	}
	if (typeof target.removeListener === 'function') {
		target.removeListener(event, handler);
	}
};

export const getIdentity = () => {
	if (!hasStorage()) return null;
	return sessionStorage.getItem(IDENTITY_KEY);
};

const setIdentity = (identity) => {
	if (!hasStorage() || !identity) return;
	sessionStorage.setItem(IDENTITY_KEY, identity);
};

const setActiveSession = (roomKey, role) => {
	if (!hasStorage()) return;
	if (roomKey) {
		sessionStorage.setItem(
			ACTIVE_ROOM_KEY,
			String(roomKey).toUpperCase()
		);
	}
	if (role) {
		sessionStorage.setItem(ACTIVE_ROLE_KEY, role);
	}
};

const waitForIdentity = () => {
	return new Promise((resolve) => {
		const current = getIdentity();
		if (current) {
			resolve(current);
			return;
		}

		const handler = (identity) => {
			removeListenerSafe(socket, 'identity provision', handler);
			resolve(identity);
		};

		socket.on('identity provision', handler);
	});
};

// Identity handshake
socket.on('identity request', () => {
	const identity = getIdentity();
	if (!identity) {
		socket.emit('identity response');
		return;
	}
	socket.emit('identity response', identity);
});

socket.on('identity provision', (identity) => {
	setIdentity(identity);
});

// Other stuff
export const subscribeToRoomDeleted = (cb) => {
	if (typeof cb !== 'function') return function () {};

	function handler(payload) {
		cb(payload);
	}

	socket.on('room-deleted', handler);

	return function () {
		removeListenerSafe(socket, 'room-deleted', handler);
	};
};

export const notifyAdminClosed = (key) => {
	try {
		socket.emit('admin-closed', {key}, getIdentity());
	} catch (e) {}
};

export const subscribeToSocketLifecycle = (handlers) => {
	const {
		onConnect,
		onDisconnect,
		onReconnectAttempt,
		onReconnect,
		onConnectError,
	} = handlers || {};

	const handleConnect = () => {
		if (onConnect) onConnect();
	};

	const handleDisconnect = (reason) => {
		if (onDisconnect) onDisconnect(reason);
	};

	const handleReconnectAttempt = (n) => {
		if (onReconnectAttempt) onReconnectAttempt(n);
	};

	const handleReconnect = () => {
		if (onReconnect) onReconnect();
	};

	const handleConnectError = (err) => {
		if (onConnectError) onConnectError(err);
	};

	socket.on('connect', handleConnect);
	socket.on('disconnect', handleDisconnect);
	socket.on('connect_error', handleConnectError);

	if (socket.io) {
		socket.io.on('reconnect_attempt', handleReconnectAttempt);
		socket.io.on('reconnect', handleReconnect);
	}

	return function () {
		removeListenerSafe(socket, 'connect', handleConnect);
		removeListenerSafe(socket, 'disconnect', handleDisconnect);
		removeListenerSafe(socket, 'connect_error', handleConnectError);

		if (socket.io) {
			removeListenerSafe(
				socket.io,
				'reconnect_attempt',
				handleReconnectAttempt
			);
			removeListenerSafe(socket.io, 'reconnect', handleReconnect);
		}
	};
};

let lastServerEpoch = null;

export function subscribeToServerInfo(cb) {
	const handler = function (info) {
		var epoch =
			info && typeof info.epoch !== 'undefined' ? info.epoch : null;

		var restarted = false;
		if (
			lastServerEpoch !== null &&
			epoch !== null &&
			epoch !== lastServerEpoch
		) {
			restarted = true;
		}

		if (epoch !== null) {
			lastServerEpoch = epoch;
		}

		if (typeof cb === 'function') {
			cb({
				epoch: epoch,
				restarted: restarted,
				serverStart:
					info && typeof info.serverStart !== 'undefined'
						? info.serverStart
						: null,
			});
		}
	};

	socket.on('server-info', handler);

	return function () {
		removeListenerSafe(socket, 'server-info', handler);
	};
}

export const requestRoomKey = async function (testKey, adminId, options) {
	await waitForIdentity();

	var normalizedKey = String(testKey).toUpperCase();
	var resume = options && options.resume === true ? true : false;

	setActiveSession(normalizedKey, 'adminUser');

	socket.emit(
		'request room key',
		{
			testKey: normalizedKey,
			adminId: adminId,
			resume: resume
		},
		getIdentity()
	);
};

export const subscribeToRoomConfig = (cb) => {
	const handler = (roomConfig) => {
		cb(roomConfig);
	};

	socket.on('room config', handler);

	return function () {
		removeListenerSafe(socket, 'room config', handler);
	};
};

export const subscribeToIdentityProvision = (cb) => {
	const handler = (identity) => {
		cb(identity);
	};

	socket.on('identity provision', handler);

	return function () {
		removeListenerSafe(socket, 'identity provision', handler);
	};
};

export const updateStage = (test, stage, step = 0, vars = {}) => {
	socket.emit('update stage', {test, stage, step, vars}, getIdentity());
};

export const updateAgeGroup = ({ageGroup}) => {
	socket.emit('update age group', {ageGroup}, getIdentity());
};

export const updateReady = (isReady) => {
	socket.emit('update ready', {isReady}, getIdentity());
};

export const updateLanguage = (language) => {
	socket.emit('update language', {language}, getIdentity());
};

export const joinRoom = async (
	roomKey,
	secret = null,
	userRole = 'testUser'
) => {
	await waitForIdentity();

	const expectedKey = String(roomKey).toUpperCase();
	setActiveSession(expectedKey, userRole);

	socket.emit('join room', expectedKey, secret, userRole, getIdentity());

	return new Promise(function (resolve, reject) {
		const cleanup = () => {
			removeListenerSafe(socket, 'invalid room key', onInvalidRoomKey);
			removeListenerSafe(socket, 'invalid room', onInvalidRoom);
			removeListenerSafe(socket, 'join room failed', onJoinRoomFailed);
			removeListenerSafe(socket, 'room config', onRoomConfig);
		};

		const onInvalidRoomKey = (err) => {
			cleanup();
			reject(err || 'invalid room key');
		};

		const onInvalidRoom = (err) => {
			cleanup();
			reject(err || 'invalid room');
		};

		const onJoinRoomFailed = (err) => {
			cleanup();
			reject(err || 'join room failed');
		};

		const onRoomConfig = (roomConfig) => {
			const incomingKey = String(
				(roomConfig && (roomConfig.key || roomConfig.testKey)) || ''
			).toUpperCase();

			if (incomingKey && incomingKey !== expectedKey) return;

			cleanup();
			resolve(roomConfig);
		};

		socket.on('invalid room key', onInvalidRoomKey);
		socket.on('invalid room', onInvalidRoom);
		socket.on('join room failed', onJoinRoomFailed);
		socket.on('room config', onRoomConfig);
	});
};

export const submitTestResults = (testId, results, authToken) => {
	socket.emit('submit results', {testId, results, authToken}, getIdentity());
};

export const submitCandidateInfo = (testKey, info, authToken, continued) => {
	socket.emit(
		'submit candidate info',
		{testKey, info, authToken, continued},
		getIdentity()
	);
};

export const updateStepData = (stepData) => {
	socket.emit('update step data', stepData, getIdentity());
};

export const startTimer = (test, stage, step) => {
	socket.emit('start timer', {test, stage, step}, getIdentity());
};

export const endTimer = (test, stage, step) => {
	socket.emit('end timer', {test, stage, step}, getIdentity());
};

// PING STUFF
export const pingServer = (pingresponse) => {
	socket.emit('ping-server', pingresponse, getIdentity());
};

export const pingClient = (pingresponse) => {
	socket.emit('ping-client', pingresponse, getIdentity());
};

export const subscribeToServerPong = (cb) => {
	const handler = (pingresponse) => {
		cb(pingresponse);
	};

	socket.on('pong-server', handler);

	return function () {
		removeListenerSafe(socket, 'pong-server', handler);
	};
};

export const subscribeToClientPong = (cb) => {
	const handler = (pingresponse) => {
		cb(pingresponse);
	};

	socket.on('pong-client', handler);

	return function () {
		removeListenerSafe(socket, 'pong-client', handler);
	};
};

export const subscribeToMasterPong = (cb) => {
	const handler = (pingresponse) => {
		cb(pingresponse);
	};

	socket.on('pong-master', handler);

	return function () {
		removeListenerSafe(socket, 'pong-master', handler);
	};
};

export const subscribeToMasterPing = (cb) => {
	const handler = (pingresponse) => {
		cb(pingresponse);
	};

	socket.on('ping-master', handler);

	return function () {
		removeListenerSafe(socket, 'ping-master', handler);
	};
};

export const subscribeToClientPing = (cb) => {
	const handler = (pingresponse) => {
		cb(pingresponse);
	};

	socket.on('ping-client', handler);

	return function () {
		removeListenerSafe(socket, 'ping-client', handler);
	};
};

export const masterPong = (payload) => {
	socket.emit('pong-master', payload, getIdentity());
};

export const clientPong = (payload) => {
	socket.emit('pong-client', payload, getIdentity());
};